<template>
  <BaseSpinner v-if="loading || fetchingPrice" />
  <LandingPage v-else-if="!shownInterested" />
  <div
    :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]"
    v-else
  >
    <Dashboard v-if="showDashboard" />
    <Onboarding v-else />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { computed, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/helpers/currency';
import { type Website, WebsiteStatusEnum } from '@/types';
import { GET_WEBSITE, GET_WEBSITE_PRICE } from '../graphql';
import Dashboard from './dashboard/index.vue';
import LandingPage from './LandingPage.vue';
import Onboarding from './onboarding/index.vue';

const website = ref<Website | null>(null);
const { t } = useI18n();

const { onResult, loading } = useQuery(GET_WEBSITE);
onResult(({ data }) => {
  website.value = data.website;
});

const shownInterested = computed(() => !!website.value);
const showDashboard = computed(
  () =>
    website.value?.status &&
    [WebsiteStatusEnum.Canceled, WebsiteStatusEnum.Live].includes(
      website.value.status,
    ),
);

const { result: websitePrice, loading: fetchingPrice } =
  useQuery(GET_WEBSITE_PRICE);

const price = computed(() => {
  const period = t(
    `global.items.${websitePrice.value?.wavyWebsiteExtensionPrice?.period?.toLowerCase() || 'month'}`,
  );
  return `${formatCurrency(
    websitePrice.value?.wavyWebsiteExtensionPrice?.price || 0,
    websitePrice.value?.wavyWebsiteExtensionPrice?.currency || 'EUR',
  )}/${period}`;
});

provide('website', website);
provide('price', price);
</script>

<style
  lang="scss"
  module
>
.base {
  max-width: 1280px;
  margin: 0 auto;
  padding: $spacing;
}
</style>
